import React from 'react';
import { clsx } from 'clsx';
import type { PredictionStatus } from '../../types';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'sm' | 'md';
  className?: string;
}

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  className,
}: BadgeProps) {
  const variants = {
    default: 'bg-white/10 text-white/60 border-white/20',
    success: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    warning: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    danger: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
    info: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30',
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-[8px]',
    md: 'px-2.5 py-1 text-[10px]',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center font-bold uppercase tracking-widest border rounded',
        variants[variant],
        sizes[size],
        className
      )}
    >
      {children}
    </span>
  );
}

interface StatusBadgeProps {
  status: PredictionStatus;
  size?: 'sm' | 'md';
}

export function StatusBadge({ status, size = 'md' }: StatusBadgeProps) {
  const statusConfig: Record<PredictionStatus, { label: string; variant: BadgeProps['variant'] }> = {
    'in-progress': { label: 'In Progress', variant: 'info' },
    success: { label: 'Success', variant: 'success' },
    fail: { label: 'Failed', variant: 'danger' },
    invalidated: { label: 'Invalidated', variant: 'default' },
  };

  const config = statusConfig[status];

  return (
    <Badge variant={config.variant} size={size}>
      {config.label}
    </Badge>
  );
}
